import { useLocation } from "wouter";
import { AppShell, Divider, NavLink, ScrollArea } from "@mantine/core";
import {
  IconKey,
  IconLogout,
  IconChevronRight,
  IconBuildingFactory,
  IconUserStar,
  IconServerCog,
  IconHome,
  IconStethoscope,
  IconTableImport,
  IconWorldDollar,
  IconPackage,
  IconSchema,
  IconChartBar,
  IconTable,
  IconLayoutGrid,
  IconCertificate,
  IconDashboard,
  IconLanguage,
  IconSend,
  IconBroadcast,
  IconPlugConnected,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useStore } from "./store";
import { userLogout } from "./api";

export default function Navbar({ toggle }) {
  const { t } = useTranslation("common");
  const [location, setLocation] = useLocation();
  const authToken = useStore((state) => state.authToken);
  const profile = useStore((state) => state.profile);

  function MyNavLink(item) {
    return (
      <NavLink
        label={item.label}
        active={item.exact ? location === item.link : location.startsWith(item.link)}
        key={item.label}
        leftSection={<item.icon stroke={1.5} />}
        rightSection={
          item.rightSection ?? <IconChevronRight size="0.8rem" stroke={1.5} />
        }
        onClick={(event) => {
          event.preventDefault();
          item.onClick ? item.onClick() : setLocation(item.link);
          toggle();
        }}
      />
    );
  }

  return (
    <>
      {authToken ? (
        <>
          <MyNavLink label={t("nav.home")} link="/home" icon={IconHome} />
          <NavLink
            label={t("nav.imports")}
            leftSection={<IconTableImport stroke={1.5} />}
            active={location.startsWith("/imports") || location.startsWith("/vendor/submissions")}
            defaultOpened={location.startsWith("/imports") || location.startsWith("/vendor/submissions")}
          >
            <MyNavLink label={t("nav.importedFiles")} link="/imports" icon={IconTableImport} />
            <MyNavLink label={t("nav.vendorSubmissions")} link="/vendor/submissions" icon={IconSend} />
          </NavLink>
          <MyNavLink label={t("nav.diagnostic")} link="/diagnose" icon={IconStethoscope} />

          <Divider label={t("nav.pim")} />
          <NavLink
            label={t("nav.products")}
            leftSection={<IconPackage stroke={1.5} />}
            active={location.startsWith("/pim/products") || location.startsWith("/pim/catalog")}
            defaultOpened={location.startsWith("/pim/products") || location.startsWith("/pim/catalog")}
          >
            <MyNavLink label={t("nav.list")} link="/pim/products" icon={IconPackage} />
            <MyNavLink label={t("nav.grid")} link="/pim/products/grid" icon={IconTable} />
            <MyNavLink label={t("nav.catalog")} link="/pim/catalog" icon={IconLayoutGrid} />
          </NavLink>
          <MyNavLink label={t("nav.dataQuality")} link="/pim/data-quality" icon={IconChartBar} />
          <NavLink
            label={t("nav.schemas")}
            leftSection={<IconSchema stroke={1.5} />}
            active={location.startsWith("/pim/schemas") || location.startsWith("/pim/channels")}
            defaultOpened={location.startsWith("/pim/schemas") || location.startsWith("/pim/channels")}
          >
            <MyNavLink label={t("nav.schemaList")} link="/pim/schemas" icon={IconSchema} />
            <MyNavLink label={t("nav.channels")} link="/pim/channels" icon={IconBroadcast} />
            <MyNavLink
              label={t("nav.storeConnections")}
              link="/settings/integrations/stores"
              icon={IconPlugConnected}
            />
          </NavLink>
          <NavLink
            label={t("nav.translations")}
            leftSection={<IconLanguage stroke={1.5} />}
            active={location.startsWith("/translations")}
            defaultOpened={location.startsWith("/translations")}
          >
            <MyNavLink label={t("nav.translationUsage")} link="/translations" icon={IconLanguage} />
            <MyNavLink label={t("nav.translateText")} link="/translations/new" icon={IconLanguage} />
            <MyNavLink label={t("nav.translationHistory")} link="/translations/requests" icon={IconLanguage} />
          </NavLink>
          {(profile?.role === "TenantAdmin" || profile?.role === "TenantOwner") && (
            <MyNavLink label={t("nav.users")} link="/users" icon={IconUserStar} />
          )}

          <AppShell.Section component={ScrollArea}>
            {profile?.role == "GlobalAdmin" && (
              <>
                <Divider label={t("nav.admin")} />
                <MyNavLink
                  label={t("nav.dashboard")}
                  link="/admin"
                  icon={IconDashboard}
                  exact
                />
                <MyNavLink
                  label={t("nav.users")}
                  link="/admin/users"
                  icon={IconUserStar}
                />
                <MyNavLink
                  label={t("nav.tenants")}
                  link="/tenants"
                  icon={IconBuildingFactory}
                />
                <MyNavLink
                  label={t("nav.jobs")}
                  link="/admin/jobs"
                  icon={IconServerCog}
                />
                <MyNavLink
                  label={t("nav.standards")}
                  link="/admin/standards"
                  icon={IconCertificate}
                />
              </>
            )}
          </AppShell.Section>
          <AppShell.Section grow></AppShell.Section>
          <AppShell.Section>
            <MyNavLink label={t("nav.logout")} icon={IconLogout} onClick={userLogout} />
          </AppShell.Section>
        </>
      ) : (
        <AppShell.Section component={ScrollArea}>
          <MyNavLink label={t("nav.login")} link="auth/login" icon={IconKey} />
        </AppShell.Section>
      )}
    </>
  );
}
